import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Trash2, X, MessageCircle, Phone } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { eur, api } from "@/lib/api";
import { usePublicConfig, waLink, telLink, formatPhone } from "@/lib/config";


const CartSupport = ({ t }) => {
  const cfg = usePublicConfig();
  if (!cfg || (!cfg.whatsapp && !cfg.phone)) return null;
  return (
    <div data-testid="cart-support" className="mt-4 border border-navy/10 bg-surface px-4 py-3 text-center">
      <p className="text-[10px] font-light leading-relaxed text-navy/55">{t.co?.supportMsg}</p>
      <div className="mt-2 flex items-center justify-center gap-4">
        {cfg.whatsapp && (
          <a href={waLink(cfg.whatsapp, t.co?.supportMsg)} target="_blank" rel="noopener noreferrer" data-testid="cart-support-whatsapp" className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-navy transition-colors hover:text-gold-dark">
            <MessageCircle size={12} /> WhatsApp
          </a>
        )}
        {cfg.phone && (
          <a href={telLink(cfg.phone)} data-testid="cart-support-phone" className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-navy transition-colors hover:text-gold-dark">
            <Phone size={12} /> {formatPhone(cfg.phone)}
          </a>
        )}
      </div>
    </div>
  );
};

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, shipping, promo, setPromo } = useCart();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);
  const [promoInput, setPromoInput] = useState(promo || "");
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!promo) {
      setQuote(null);
      return;
    }
    api.promoQuote(promo).then(setQuote).catch(() => setQuote({ invalid: true }));
  }, [promo]);

  const discount = quote && !quote.invalid
    ? quote.percent_off
      ? (subtotal * quote.percent_off) / 100
      : Math.min(quote.amount_off || 0, subtotal)
    : 0;
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const sideClass = dir === "rtl" ? "left-0" : "right-0";
  const hiddenX = dir === "rtl" ? "-100%" : "100%";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
            className="fixed inset-0 z-[60] bg-navy/40 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: hiddenX }}
            animate={{ x: 0 }}
            exit={{ x: hiddenX }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cart-drawer"
            className={`fixed top-0 ${sideClass} z-[70] flex h-full w-full max-w-md flex-col bg-white shadow-2xl`}
          >
            <div className="flex items-center justify-between border-b border-navy/10 px-6 py-5">
              <h2 className="font-display text-2xl font-medium text-navy">{t.cart.title}</h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setOpen(false)}
                className="p-2 text-navy/50 transition-colors hover:text-navy"
                aria-label="Close cart"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6">
                <ShoppingBag size={40} strokeWidth={1} className="text-navy/20" />
                <p data-testid="cart-empty-message" className="text-sm text-navy/50">{t.cart.empty}</p>
                <Link
                  to="/shop"
                  data-testid="cart-empty-cta"
                  onClick={() => setOpen(false)}
                  className="btn-outline-luxe"
                >
                  {t.cart.emptyCta}
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {items.map((item) => (
                    <div key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-4 border-b border-navy/5 py-5">
                      <Link to={`/product/${item.slug}`} onClick={() => setOpen(false)} className="h-24 w-20 shrink-0 overflow-hidden border border-navy/10 bg-surface">
                        <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                      </Link>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-navy">{item.name}</p>
                          <button
                            data-testid={`cart-remove-${item.slug}`}
                            onClick={() => removeItem(item.key)}
                            className="text-navy/30 transition-colors hover:text-red-500"
                            aria-label={t.cart.remove}
                          >
                            <Trash2 size={15} strokeWidth={1.5} />
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-navy/45">{eur(item.price)} · {item.variant} {t.fmt[item.unit] || t.fmt.caps}</p>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center border border-navy/15">
                            <button
                              data-testid={`cart-qty-minus-${item.slug}`}
                              onClick={() => updateQty(item.key, item.qty - 1)}
                              className="p-1.5 text-navy/60 hover:text-gold-dark disabled:opacity-30"
                              disabled={item.qty <= 1}
                            >
                              <Minus size={13} />
                            </button>
                            <span data-testid={`cart-qty-${item.slug}`} className="w-8 text-center text-xs font-semibold">{item.qty}</span>
                            <button
                              data-testid={`cart-qty-plus-${item.slug}`}
                              onClick={() => updateQty(item.key, item.qty + 1)}
                              className="p-1.5 text-navy/60 hover:text-gold-dark"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                          <p className="text-sm font-semibold text-navy">{eur(item.price * item.qty)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-navy/10 px-6 py-6">
                  {items.some((i) => i.cs) && (
                    <p data-testid="cart-preorder-note" className="mb-4 border border-gold/50 bg-gold/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-gold-dark">
                      {t.preorderNote}
                    </p>
                  )}
                  <div className="flex justify-between text-sm text-navy/60">
                    <span>{t.cart.subtotal}</span>
                    <span data-testid="cart-subtotal">{eur(subtotal)}</span>
                  </div>
                  {quote && !quote.invalid && discount > 0 && (
                    <div className="mt-2 flex justify-between text-sm font-semibold text-gold-dark" data-testid="cart-discount-line">
                      <span>{t.cart.discount} ({promo})</span>
                      <span>−{eur(discount)}</span>
                    </div>
                  )}
                  {quote?.restricted && (
                    <p data-testid="cart-restricted-hint" className="mt-1 text-[10px] uppercase tracking-[0.12em] text-navy/40">{t.acc3.restrictedHint}</p>
                  )}
                  {quote?.invalid && (
                    <p data-testid="cart-promo-invalid" className="mt-2 text-xs text-red-600">{t.cart.promoInvalid}</p>
                  )}
                  <div className="mt-2 flex justify-between text-sm text-navy/60">
                    <span>{t.cart.shipping}</span>
                    <span data-testid="cart-shipping">{shipping === 0 ? t.cart.free : eur(shipping)}</span>
                  </div>
                  <div className="mt-4 flex justify-between border-t border-navy/10 pt-4 text-base font-semibold text-navy">
                    <span>{t.cart.total}</span>
                    <span data-testid="cart-total">{eur(subtotal - discount + shipping)}</span>
                  </div>
                  <div className="mt-5 flex gap-2">
                    <input
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      placeholder={t.cart.promoPh}
                      data-testid="cart-promo-input"
                      className="input-luxe flex-1 !py-2.5 font-mono text-xs uppercase tracking-wider"
                    />
                    <button
                      data-testid="cart-promo-apply"
                      onClick={() => setPromo(promoInput.trim())}
                      className="btn-outline-luxe !px-4 !py-2.5"
                    >
                      {t.cart.promoApply}
                    </button>
                  </div>
                  {promo && (
                    <p data-testid="cart-promo-applied" className="mt-2 text-xs font-semibold text-gold-dark">
                      {t.cart.promoApplied}: {promo}
                    </p>
                  )}
                  <button
                    data-testid="cart-checkout-button"
                    onClick={() => {
                      setOpen(false);
                      navigate("/checkout");
                    }}
                    className="btn-primary-luxe mt-6 w-full"
                  >
                    {t.cart.checkout}
                  </button>
                  <button
                    data-testid="cart-continue-button"
                    onClick={() => setOpen(false)}
                    className="mt-3 w-full text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/50 transition-colors hover:text-gold-dark"
                  >
                    {t.cart.continue}
                  </button>
                  <CartSupport t={t} />
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
